'use client';

import { useTranslations } from 'next-intl';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { cn } from '@/app/utils';

interface MistakeItem {
  id: string;
  title: string;
  description: string;
  videoSrc: string;
  poster: string;
  mobilePoster: string;
}

const INACTIVE_W_EXPR = 'clamp(8rem,15vw,12rem)';
const GAP_REM = 0.5;

export function CommonMistakesSection() {
  const t = useTranslations('ai-image-to-video');

  const mistakes: MistakeItem[] = [
    {
      id: 'lowQualitySource',
      title: t('mistakes.lowQualitySource.title'),
      description: t('mistakes.lowQualitySource.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/low_quality-41629de9.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/low_quality-fefd88c2.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/low_quality-fefd88c2-f1838ca6.webp',
    },
    {
      id: 'smallSubject',
      title: t('mistakes.smallSubject.title'),
      description: t('mistakes.smallSubject.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/unclear-bfb1d19d.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/unclear-5aef2ca6.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/unclear-5aef2ca6-c919fccc.webp',
    },
    {
      id: 'overloadedPrompt',
      title: t('mistakes.overloadedPrompt.title'),
      description: t('mistakes.overloadedPrompt.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/overloaded-0a316d09.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/overloaded-ac506f4e.webp',
      mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/overloaded-ac506f4e-12b0bd82.webp',
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);

  const inactiveCount = mistakes.length - 1;
  const desktopRowStyle: CSSProperties = {
    height: `calc((100cqi - ${inactiveCount} * ${INACTIVE_W_EXPR} - ${inactiveCount} * ${GAP_REM}rem) * 3 / 4)`,
  };

  return (
    <section className="pt-[120px] sm:pt-10">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center px-8">
        <div className="mb-16 text-center sm:mb-8 sm:w-full">
          <h2 className="font-semibold text-[40px] leading-tight sm:text-[36px]">{t('mistakes.title')}</h2>
        </div>

        {/* Desktop: 横向手风琴 */}
        <div className="mx-auto w-full max-w-[960px] sm:hidden" style={{ containerType: 'inline-size' }}>
          <div className="flex w-full" style={{ ...desktopRowStyle, columnGap: `${GAP_REM}rem` }}>
            {mistakes.map((item, index) => {
              const active = index === activeIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={cn(
                    'relative h-full min-w-0 cursor-pointer overflow-hidden rounded-2xl',
                    'transition-[flex-basis,flex-grow] duration-500 ease-[cubic-bezier(0.33,1,0.68,1)]',
                    active ? 'flex-[1_1_0%]' : 'flex-[0_0_clamp(8rem,15vw,12rem)] shrink-0',
                  )}
                >
                  <div className="relative h-full w-full overflow-hidden rounded-[inherit]">
                    <div className="absolute inset-0 bg-black">
                      {active && item.videoSrc ? (
                        <VideoLazyLoad
                          videoSrc={item.videoSrc}
                          poster={item.poster}
                          mobilePoster={item.mobilePoster}
                          className="h-full w-full"
                          videoClassName="!object-contain"
                          alt={item.title}
                        />
                      ) : item.poster ? (
                        <img src={item.poster} alt={item.title} loading="lazy" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-white/6" />
                      )}
                    </div>

                    {active ? (
                      <>
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-50% via-black/30 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 p-6 text-left">
                          <h3 className="font-semibold text-[22px] text-white leading-[1.3]">{item.title}</h3>
                          <p className="max-w-[360px] text-sm text-white/60 leading-[1.6]">{item.description}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-black/60" />
                        <div className="absolute bottom-6 left-6">
                          <span className="-rotate-90 block origin-bottom-left whitespace-nowrap font-medium text-sm text-white/80">
                            {item.title}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile: 纵向手风琴 */}
        <div className="hidden flex-col gap-2 sm:flex">
          {mistakes.map((item, index) => {
            const active = index === activeIndex;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={cn(
                  'relative w-full cursor-pointer overflow-hidden rounded-xl',
                  !active && 'transition-colors duration-300',
                )}
              >
                {active ? (
                  <div className="relative aspect-[4/3] w-full">
                    <div className="absolute inset-0 bg-black">
                      <VideoLazyLoad
                        videoSrc={item.videoSrc}
                        poster={item.poster}
                        mobilePoster={item.mobilePoster}
                        className="h-full w-full"
                        videoClassName="!object-contain"
                        alt={item.title}
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-50% via-black/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-1.5 p-4 text-left">
                      <h3 className="font-semibold text-lg text-white leading-[1.3]">{item.title}</h3>
                      <p className="text-white/60 text-xs leading-[1.5]">{item.description}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex w-full items-center bg-white/[0.04] px-4 py-3 text-left">
                    <span className="min-w-0 flex-1 font-medium text-sm text-white/70">{item.title}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
